/**
 * Milestone bonuses — the ladder as STEPS UP THE MOUNTAIN (the climb).
 *
 *  - Rep view (default): that rep's YTD as a climber standing on the steps —
 *    cleared rungs green with a check, the rung in play tinted brand, summit
 *    flag past the top — with the unlock strip pinned at the foot.
 *  - All players: the whole roster on the ladder — total bonuses up top, each
 *    rep's rung + earned, paginated so the card never grows.
 *
 * ONE control: the header dropdown. It opens on the first player; "All
 * players" is the last option, never the default. Players get their own view
 * with no dropdown at all.
 *
 * Data comes from GET /group/:id/bonus-progress via ctx.bonus:
 *   { connected, isAdmin, myId, config:{milestones,...}, reps:[...], totals:{...} }
 *
 * `sample` prop (used by Team Room → Milestones & bonuses as its live preview):
 *   { ytd, milestones } — renders the anonymous rep view only, no controls, so
 *   the setup page previews the REAL widget from the ladder being edited.
 */
import React, { useEffect, useRef, useState } from "react";
import SoftBox from "../SoftBox";
import CardHeader, { CARD_ROOT, CARD_BODY_PAD } from "../CardHeader";
import { UseIsAdmin } from "../../hooks/auth";
import { FONT, C, money, compact, usePageSize, LightPicker, Pager, Avatar, hueFor } from "../SalesBonus/common";
import { TideLadder, TrackLadder } from "../SalesBonus/ladderVisuals";
import { programFromConfig, hasLadder } from "../SalesBonus/moneyRules";
import { realReps } from "../../helpers/teamAverage";
import { DEMO_MILESTONES } from "../../api/bonusSnapshot";

// Hero number counts up (950ms, cubic ease-out) on mount and whenever the
// value changes — the proto's chosen entrance. Instant under reduced motion.
function useCountUp(target) {
  const [val, setVal] = useState(0);
  const fromRef = useRef(0);
  const ranRef = useRef(false);
  useEffect(() => {
    const from = fromRef.current;
    fromRef.current = target;
    const reduced =
      typeof window !== "undefined" && window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    // Entrance only. Re-animating on every value change made the Team Room
    // preview's number churn on each keystroke while editing the ladder.
    if (reduced || from === target || ranRef.current) {
      ranRef.current = true;
      setVal(target);
      return undefined;
    }
    ranRef.current = true;
    const t0 = performance.now();
    const DUR = 950;
    let raf;
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / DUR);
      const eased = 1 - (1 - p) ** 3;
      setVal(Math.round(from + (target - from) * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target]);
  return val;
}

const Strong = ({ children }) => (
  <b style={{ fontWeight: 500, color: C.ink, fontVariantNumeric: "tabular-nums" }}>{children}</b>
);

// One line of the track variant's story box: icon chip + sentence.
function StoryLine({ tone, icon, children, primary }) {
  // The mint chip glows on the dark TV — let it fall to the dark tile there
  // (the green glyph stays legible on both grounds); light mode is untouched.
  const bg = tone === "green" ? "var(--tvd-tile, #EAF6F0)" : `${primary}14`;
  const fg = tone === "green" ? "#12805C" : primary;
  return (
    <SoftBox sx={{ display: "flex", alignItems: "center", gap: "9px", fontSize: "12.5px", color: "#475467" }}>
      <span style={{ width: 22, height: 22, borderRadius: "7px", flex: "none", display: "flex", alignItems: "center", justifyContent: "center", background: bg, color: fg }}>
        {icon}
      </span>
      <span>{children}</span>
    </SoftBox>
  );
}

const IconCheck = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
    <path d="M20 6L9 17l-5-5" />
  </svg>
);
const IconAhead = (
  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
    <path d="M12 19V5M5 12l7-7 7 7" />
  </svg>
);

const ROW_H = 62;
const ALL = "__all";

// Safety net only: unconfigured workspaces render the setup face (below), so
// these rungs never show as data — they just keep the math crash-free if a
// config somehow arrives with an empty milestones array.
const DEFAULT_LADDER = DEMO_MILESTONES;

const initialsOf = (name) =>
  String(name || "")
    .split(" ")
    .map((w) => w[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

function Shell({ header = null, children }) {
  return (
    <SoftBox sx={{ ...CARD_ROOT, minHeight: "220px" }}>
      {/* The house header is flush to the card's edges, so the side padding
          belongs to the body — not to the card root. See CardHeader. */}
      {header}
      <SoftBox sx={{ flex: "1 1 auto", display: "flex", flexDirection: "column", minHeight: 0, padding: CARD_BODY_PAD }}>
        {children}
      </SoftBox>
    </SoftBox>
  );
}

/* No ladder configured. Ghost steps carry the shape without ever printing a
   target or a bonus — a made-up rung would read as a pay promise. */
function LadderSetupState({ title, isAdmin, workspaceName }) {
  return (
    <Shell header={<Head picker={null} title={title}/>}>
      <SoftBox sx={{ flex: "1 1 auto", minHeight: 0, display: "flex", flexDirection: "column", justifyContent: "center", px: "2px" }}>
        {isAdmin ? (
          <>
            <span style={{ fontSize: "15px", fontWeight: 600, color: C.ink }}>
              No ladder yet for {workspaceName || "this workspace"}
            </span>
            <span style={{ fontSize: "12px", color: C.muted, marginTop: "4px", lineHeight: 1.55 }}>
              Pick what it climbs and set the rungs — no numbers show until you do.
            </span>
            <SoftBox sx={{ mt: "18px", display: "flex", alignItems: "flex-end", gap: "10px", px: "6px" }}>
              {[34, 52, 70].map((h) => (
                <SoftBox
                  key={h}
                  sx={{
                    flex: 1,
                    height: `${h}px`,
                    borderRadius: "6px 6px 0 0",
                    border: "1px dashed var(--tvd-hair, #E4E8EE)",
                    borderBottom: 0,
                    background: "var(--tvd-tile, repeating-linear-gradient(45deg,#F1F3F7,#F1F3F7 6px,#F8FAFC 6px,#F8FAFC 10px))",
                  }}
                />
              ))}
            </SoftBox>
            <span style={{ fontSize: "11px", color: C.faint, marginTop: "14px" }}>
              Open this card&rsquo;s settings from Customize to build the ladder.
            </span>
          </>
        ) : (
          <span style={{ fontSize: "12.5px", color: C.faint, textAlign: "center" }}>
            Your team&rsquo;s bonus ladder is being set up.
          </span>
        )}
      </SoftBox>
    </Shell>
  );
}

function Head({ picker, title = "Milestone bonuses" }) {
  return (
    <CardHeader
      title={title}
      right={
        <>
          <span style={{ fontSize: "10.5px", fontWeight: 500, color: C.faint }}>Year to date · {new Date().getFullYear()}</span>
          {picker}
        </>
      }
    />
  );
}

/** The rep body — hero, ladder visual, story/pace strip, pinned unlock strip.
 *  Shared by real + sample, and by both widgets: variant "climb" (steps) or
 *  "track" (rail, with the story box). */
function RepBody({ ytd, earned, milestones, initials, image = null, primary, variant = "climb", showEarned = true, clearedMonths = null, metricName = null }) {
  const shownYtd = useCountUp(ytd);
  const TOP = milestones[milestones.length - 1].target;
  const pct = Math.max(0, Math.min(100, (ytd / TOP) * 100));
  const nextRung = milestones.find((m) => ytd < m.target) || null;
  const lastCleared = [...milestones].filter((m) => ytd >= m.target).pop() || null;
  const when = clearedMonths && lastCleared ? clearedMonths[lastCleared.target] : null;

  return (
    <>
      <SoftBox sx={{ flex: "0 0 auto", mt: "6px" }}>
        <span style={{ fontSize: "20px", fontWeight: 600, letterSpacing: "-0.01em", color: C.ink, fontVariantNumeric: "tabular-nums" }}>{money(shownYtd)}</span>
        <span style={{ fontSize: "12.5px", color: C.muted, marginLeft: "8px" }}>· {Math.round(pct)}% of {compact(TOP)}</span>
        {metricName && (
          <div style={{ fontSize: "12px", fontWeight: 500, color: C.muted, marginTop: "2px" }}>
            {metricName} year to date
          </div>
        )}
        {showEarned && (
          <div style={{ fontSize: "12px", fontWeight: 500, color: C.muted, marginTop: "2px" }}>
            Earned so far · <span style={{ fontWeight: 600, color: C.green }}>{money(earned)}</span>
          </div>
        )}
      </SoftBox>

      <SoftBox sx={{ flex: "1 1 auto", minHeight: 0, mt: "6px", display: "flex", flexDirection: "column", justifyContent: variant === "track" ? "center" : "flex-end" }}>
        {variant === "track" ? (
          <TrackLadder ytd={ytd} milestones={milestones} primary={primary} />
        ) : (
          <TideLadder ytd={ytd} milestones={milestones} initials={initials} image={image} primary={primary} />
        )}
      </SoftBox>

      {/* The "Cleared … · at this pace … lands in …" pace story is hidden for
          everyone (admins AND players) — it read as a pay-date promise. */}
      {variant === "track" && (
        <SoftBox sx={{ flex: "0 0 auto", mt: "6px", background: "#F8FAFC", border: "1px solid #EEF1F5", borderRadius: "10px", padding: "11px 14px", display: "flex", flexDirection: "column", gap: "7px" }}>
          {lastCleared ? (
            <StoryLine tone="green" icon={IconCheck} primary={primary}>
              Cleared {compact(lastCleared.target)}{when ? ` in ${when}` : ""} · <Strong>+{money(lastCleared.bonus)} paid</Strong>
            </StoryLine>
          ) : (
            <StoryLine tone="brand" icon={IconAhead} primary={primary}>
              First milestone still ahead{nextRung ? <> — <Strong>{money(nextRung.target - ytd)}</Strong> to go</> : null}
            </StoryLine>
          )}
          {!nextRung && (
            <StoryLine tone="green" icon={<span style={{ fontSize: "11px" }}>★</span>} primary={primary}>
              Every milestone unlocked this year
            </StoryLine>
          )}
        </SoftBox>
      )}

      <SoftBox
        sx={{
          flex: "0 0 auto",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mt: "10px",
          pt: "11px",
          borderTop: `1px solid ${C.line}`,
          fontSize: "12.5px",
        }}
      >
        {nextRung ? (
          <>
            {/* "to unlock" names the BONUS the player gets, not the milestone
                target — "$91,062 to unlock $700K" read like the prize was the
                revenue number itself. */}
            <span style={{ fontWeight: 500, color: C.muted }}>
              <span style={{ fontWeight: 500, color: C.ink, fontVariantNumeric: "tabular-nums" }}>{money(nextRung.target - ytd)}</span> to unlock{" "}
              {/* The prize gets the money colour and extra weight — it's the
                  line's payoff, not a passing figure. */}
              <span style={{ fontWeight: 700, color: C.green, fontVariantNumeric: "tabular-nums" }}>{compact(nextRung.bonus)}</span>
            </span>
          </>
        ) : (
          <>
            <span style={{ fontWeight: 500, color: C.muted }}>Top of the ladder</span>
            <span style={{ fontWeight: 600, color: C.green }}>All bonuses earned</span>
          </>
        )}
      </SoftBox>
    </>
  );
}

export default function MilestoneBonuses({ data, primary = "#1B598E", sample = null, variant = "climb", title = "Milestone bonuses" }) {
  const connected = !!(data && data.connected);
  // Real logged-in role — false for players AND for an admin viewing as a
  // player, so the picker stays out of the player view.
  const isAdmin = UseIsAdmin();
  // Team average is never a person to pick from here — same rule as
  // AnticipatedEarnings.
  const reps = realReps((data && data.reps) || []);
  const totals = (data && data.totals) || {};
  const config = (data && data.config) || {};
  const myId = data && data.myId;
  const cfgMilestones = [...((sample ? sample.milestones : config.milestones) || [])].sort((a, b) => a.target - b.target);
  const milestones = cfgMilestones.length ? cfgMilestones : DEFAULT_LADDER;
  const TOP = milestones[milestones.length - 1].target;
  // The ladder metric's display name (payload carries the workspace KPI names,
  // same as AnticipatedEarnings) — "Estimate Won Value", not a generic
  // "revenue".
  const ladderKpiId = programFromConfig(config).scoredOnKpiId || config.revenueKpiId || null;
  const ladderName = ((config && config.kpiNames) || {})[ladderKpiId] || "revenue";

  // ONE selection: a rep's ownerId, or ALL. Admins land on a blank "Select
  // player" picker — never a preselected person, never the team average.
  // Players are pinned to themselves.
  const canPick = isAdmin && connected && reps.length > 0 && !sample;
  const [sel, setSel] = useState(() => (!isAdmin && myId) ? myId : "");
  // If the selected rep drops off the roster (or there's no roster yet),
  // fall back to blank rather than guessing a new one for the admin.
  useEffect(() => {
    if (!isAdmin) return;
    if (!reps.length) { if (sel !== "") setSel(""); return; }
    const valid = sel === "" || sel === ALL || reps.some((r) => r.ownerId === sel);
    if (!valid) setSel("");
  }, [reps, sel, isAdmin]);
  const [page, setPage] = useState(0);
  const midRef = useRef(null);
  const showAll = canPick && sel === ALL;
  const showBlank = canPick && sel === "";
  // reps.length in the deps: re-measure when the roster loads so the last
  // row is never clipped (page size was computed against an empty list).
  const pageSize = usePageSize(midRef, ROW_H, [showAll, reps.length]);
  const pages = Math.max(1, Math.ceil(reps.length / pageSize));
  const curPage = Math.min(page, pages - 1);
  useEffect(() => setPage(0), [pageSize, showAll, reps.length]);

  /* No ladder for this workspace yet → the setup face. After all hooks, and
     never for the sample preview (which supplies its own ladder). Applies to
     unconfigured workspaces too (!connected) — an unconfigured card must show
     its honest empty state, never the demo/default rungs. */
  if (!sample && (!connected || !hasLadder(programFromConfig(config)))) {
    return <LadderSetupState title={title} isAdmin={isAdmin} workspaceName={data && data.workspaceName} />;
  }

  // ---- SAMPLE (Team Room live preview): anonymous, control-free ----
  if (sample) {
    return (
      <Shell header={<Head picker={null} title={title}/>}>
        <RepBody ytd={sample.ytd || 0} earned={milestones.filter((m) => (sample.ytd || 0) >= m.target).reduce((s, m) => s + m.bonus, 0)} milestones={milestones} initials="" primary={primary} variant={variant} />
      </Shell>
    );
  }

  const picker = canPick ? (
    <LightPicker
      small
      width="150px"
      options={[
        ...reps.map((r) => ({ value: r.ownerId, label: r.name })),
        { value: ALL, label: "All players" },
      ]}
      value={sel}
      onChange={setSel}
      placeholder="Select player"
    />
  ) : null;

  // ---- ALL PLAYERS (the roster on the ladder) ----
  if (showAll) {
    const sorted = [...reps].sort((a, b) => b.ytd - a.ytd);
    const rows = sorted.slice(curPage * pageSize, curPage * pageSize + pageSize);
    return (
      <Shell header={<Head picker={picker} title={title}/>}>
        <SoftBox sx={{ flex: "0 0 auto", mt: "6px" }}>
          <span style={{ fontSize: "22px", fontWeight: 600, letterSpacing: "-0.02em", color: C.ink, fontVariantNumeric: "tabular-nums" }}>{money(totals.bonuses)}</span>
          <span style={{ fontSize: "12.5px", color: C.muted, marginLeft: "8px" }}>· bonuses earned so far</span>
          <div style={{ fontSize: "12px", color: C.muted, marginTop: "2px" }}>
            {compact(totals.ytd)} team {ladderName} year to date
          </div>
          <div style={{ height: 1, background: C.line, margin: "12px 0 8px" }} />
          <div style={{ fontSize: "10.5px", fontWeight: 500, color: C.faint }}>
            Where each player is on the ladder
          </div>
        </SoftBox>

        <SoftBox ref={midRef} sx={{ flex: "1 1 auto", minHeight: 0, overflow: "hidden" }}>
          {rows.length === 0 && (
            <SoftBox sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: "100%", color: C.faint, fontSize: "13px" }}>
              No {ladderName} tracked yet.
            </SoftBox>
          )}
          {rows.map((r) => {
            const done = milestones.filter((m) => r.ytd >= m.target).slice(-1)[0];
            const rung = done
              ? `${compact(done.target)} ✓ · next ${r.nextTarget ? compact(r.nextTarget) : "max"}`
              : `next ${r.nextTarget ? compact(r.nextTarget) : "—"}`;
            const pct = Math.max(0, Math.min(100, (r.ytd / TOP) * 100));
            return (
              <SoftBox key={r.ownerId} sx={{ padding: "8px 0", borderTop: `1px solid ${C.soft}`, "&:first-of-type": { borderTop: "none" } }}>
                <SoftBox sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
                  <Avatar name={r.name} image={r.image} color={hueFor(r.ownerId)} />
                  <span style={{ flex: 1, minWidth: 0, fontSize: "13px", fontWeight: 500, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.name}</span>
                  <span style={{ flexShrink: 0, fontSize: "11px", fontWeight: 600, color: primary, background: `${primary}17`, borderRadius: "6px", padding: "2px 8px", whiteSpace: "nowrap" }}>
                    {rung}
                  </span>
                  <span style={{ fontSize: "13px", fontWeight: 600, color: r.earned ? C.green : C.faint, minWidth: "52px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>
                    {money(r.earned)}
                  </span>
                </SoftBox>
                <div style={{ height: 5, borderRadius: 99, background: C.soft, margin: "7px 0 0 38px", position: "relative", overflow: "hidden" }}>
                  <div style={{ position: "absolute", inset: 0, right: "auto", width: `${pct}%`, borderRadius: 99, background: primary }} />
                </div>
                <div style={{ display: "flex", justifyContent: "space-between", marginLeft: "38px", marginTop: "5px", fontSize: "10.5px", color: C.faint }}>
                  <span>{money(r.ytd)} YTD</span>
                  <span>{r.nextTarget ? `${money(r.nextTarget - r.ytd)} to ${compact(r.nextTarget)}` : "ladder complete"}</span>
                </div>
              </SoftBox>
            );
          })}
        </SoftBox>

        <SoftBox sx={{ flex: "0 0 auto", display: "flex", alignItems: "center", justifyContent: "space-between", mt: "10px", pt: "11px", borderTop: `1px solid ${C.line}` }}>
          <span>
            <span style={{ fontSize: "12px", fontWeight: 500, color: C.muted }}>Total bonuses </span>
            <span style={{ fontSize: "15px", fontWeight: 600, color: C.ink, fontVariantNumeric: "tabular-nums" }}>{money(totals.bonuses)}</span>
          </span>
          <Pager page={curPage} pages={pages} setPage={setPage} />
        </SoftBox>
      </Shell>
    );
  }

  // ---- REP view (the climb) ----
  /* Blank ("Select player") state: render the SAME climb visual as a real
     rep — ladder, unlock strip — just at $0, instead of a plain empty
     message, until an admin actually picks someone. `rep` stays null here
     (rather than a fabricated row) since RepBody already renders a correct
     $0 climber for a null rep via repYtd/repEarned below. */
  const rep = showBlank
    ? null
    : (reps.find((r) => r.ownerId === sel) || reps.find((r) => r.ownerId === myId) || reps[0] || null);
  // A ladder scored on a company-wide rollup (e.g. Total Revenue, Team Hours)
  // is never owned by any one person (server never attributes it to a user),
  // so no rep's card can show real progress on it — not even a rep who does
  // have a roster row from unrelated per-user commissions. Fall back to the
  // workspace total rather than a phantom $0 climber. `!reps.length` still
  // covers workspaces where the flag isn't set (e.g. this metric has zero
  // rows yet — a brand-new integration) but there's also no roster to prefer.
  const showWorkspaceTotal = config.ladderIsCompanyWide || (!rep && !reps.length);
  const repYtd = rep ? rep.ytd : 0;
  const repEarned = rep ? rep.earned : 0;
  return (
    <Shell header={<Head picker={picker} title={title}/>}>
      <RepBody
        ytd={showWorkspaceTotal ? (totals.ytd || 0) : repYtd}
        earned={showWorkspaceTotal ? (totals.bonuses || 0) : repEarned}
        milestones={milestones}
        initials={rep ? initialsOf(rep.name) : ""}
        image={rep ? rep.image : null}
        primary={primary}
        variant={variant}
        clearedMonths={rep ? rep.clearedMonths : null}
        metricName={ladderName}
      />
    </Shell>
  );
}
